import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { Flex } from "../../../components/Grid";
import { Modal } from "../../../components/Modal";
import {
  Caption1,
  Footnote,
  Headline,
  LargeTitle,
  Subhead,
} from "../../../components/Typography";
import { Button } from "../../../components/buttons/Button";
import { MarketplaceListing } from "../../../dropTypes";
import { useAnalytics } from "../../../hooks/analytics";
import {
  useFirestoreCacheKey,
  useFirestoreQuery,
} from "../../../hooks/firestore";
import { useDisableScroll } from "../../../hooks/scroll";
import { useSnackbar } from "../../../hooks/snackbar";
import { useSocialWalletAddress } from "../../../hooks/wallet";
import {
  buyPrimaryWithSocialWalletDebit,
  buyPrimaryWithSocialWalletDebitRetry,
  buyWithSocialWallet,
  fetchDropById,
  fetchMetadataByMintId,
} from "../../../lib/backend";
import type { FetchWhereArgs } from "../../../lib/firestore";
import { smallScreenAndAbove } from "../../../lib/mediaQuery";
import { formatDollarsFromCents, withRetry } from "../../../utils";
import { getPaymentTokenBalance } from "../../../utils/web3";
import { LoadingPlaceholder } from "../../progress";
import { Panel } from "../panel";
import { ManageFunds } from "./manage";

interface PrimaryPurchaseModalProps {
  dropId: string;
  open: boolean;
  onRequestClose: () => void;
  onSuccess: (intentId: string) => void;
}

export const PrimaryPurchaseModal = ({
  dropId,
  open,
  onRequestClose,
  onSuccess,
}: PrimaryPurchaseModalProps) => {
  const dropQuery = useQuery(["drop", dropId], () => fetchDropById(dropId));
  const drop = dropQuery.data;
  const creator = drop?.variants?.[0]?.collectionMetadata.creators[0];

  const socialWalletAddress = useSocialWalletAddress();
  const balanceQuery = useQuery(
    ["escrow_payment_account", socialWalletAddress],
    () => getPaymentTokenBalance(connection, socialWalletAddress)
  );

  const hasSufficientFunds = useMemo(() => {
    if (!balanceQuery.data || !drop?.variants[0]?.priceUsdCents) return false;
    return balanceQuery.data >= Number(drop.variants[0].priceUsdCents) * 10_000;
  }, [balanceQuery.data, drop]);

  const { connection } = useConnection();
  const queryClient = useQueryClient();
  const pushSnack = useSnackbar();
  const analytics = useAnalytics();

  const mutation = useMutation(
    async () => {
      try {
        const data = await buyPrimaryWithSocialWalletDebit(dropId);
        return data;
      } catch (err) {
        console.log(err);
        if (err.message.includes("Mint failed. Please reuse signature: ")) {
          const signature = err.message
            .replace("Mint failed. Please reuse signature: ", "")
            .trim();
          console.log("retrying with signature: ", signature);
          const data = await withRetry(
            () => buyPrimaryWithSocialWalletDebitRetry(dropId, signature),
            3
          );
          return data;
        } else {
          throw err;
        }
      }
    },
    {
      onError(err) {
        console.log("error: ", err);
        pushSnack({
          key: "buy-now-error",
          message: "Something went wrong",
          type: "error",
        });
      },
      async onSuccess(data: any) {
        await queryClient.invalidateQueries([
          "escrow_payment_account",
          socialWalletAddress,
        ]);

        analytics.trackRevenue(Number(drop.variants[0].priceUsdCents) / 100, {
          properties: {
            drop_id: drop.id,
            collection_mint_id: drop.variants?.[0]?.collectionMint,
            artist: creator?.slug,
          },
        });

        onSuccess(data.mint.mint);
      },
    }
  );

  const [deposit, setDeposit] = useState(false);

  function renderContent() {
    if (dropQuery.isLoading || dropQuery.isLoading) {
      return <LoadingPlaceholder />;
    }

    if (deposit) {
      return <ManageFunds onBackClick={() => setDeposit(false)} />;
    }

    return (
      <Flex
        direction="column"
        justify="flex-start"
        height="100%"
        padding="xs"
        paddingTop="md"
        paddingBottom="md"
        mdPadding="md"
        css={`
          position: relative;
        `}
      >
        <Headline
          css={`
            text-align: center;
            margin-bottom: var(--spacing-md);
          `}
        >
          Purchase Vault
        </Headline>
        <Panel>
          <Flex direction="column" width="100%">
            <Footnote
              css={`
                text-align: left;
              `}
            >
              Vault Balance
            </Footnote>
            <Flex align="center" justify="center">
              <Flex flex={1}>
                <LargeTitle>
                  {balanceQuery.data
                    ? formatDollarsFromCents(balanceQuery.data / 10_000)
                    : "$0.00"}
                </LargeTitle>
              </Flex>
              <Flex width="110px">
                <Button
                  border
                  shape="rectangle"
                  variant="semi-transparent"
                  fontSize={13}
                  css={`
                    margin-top: 0;
                    height: 32px !important;
                    padding: var(--spacing-sm);
                    white-space: nowrap;
                  `}
                  onClick={() => setDeposit(true)}
                >
                  Add Funds
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Panel>
        <Divider />
        <Flex>
          <Image
            alt="Vault Image"
            src={drop.data.image}
            height={42}
            width={42}
            style={{
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "4px",
            }}
          />
          <Flex
            direction="column"
            css={`
              padding-left: var(--spacing-sm);
            `}
          >
            <Headline>{drop.data.name}</Headline>
            <Subhead
              css={`
                color: var(--highlight-text);
                font-weight: 600;
                text-align: left;
              `}
            >
              {creator.name}
            </Subhead>
          </Flex>
        </Flex>
        <Divider />
        <Footnote
          css={`
            text-align: left;
          `}
        >
          Total
        </Footnote>
        <LargeTitle
          css={`
            text-align: left;
          `}
        >
          {formatDollarsFromCents(Number(drop.variants[0].priceUsdCents))}
        </LargeTitle>
        <Caption1
          css={`
            text-align: left;
            margin-top: var(--spacing-md);
          `}
        >
          By purchasing you agree to our{" "}
          <a href="/terms-of-service" target="_blank">
            terms of use
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank">
            privacy policy
          </a>
          .
        </Caption1>
        <Divider />
        <div
          css={`
            margin-top: auto;
          `}
        >
          <Button
            shape="rectangle"
            variant="primary"
            loading={mutation.isLoading}
            disabled={!hasSufficientFunds}
            onClick={() => mutation.mutate()}
          >
            Pay {formatDollarsFromCents(Number(drop.variants[0].priceUsdCents))}
          </Button>
          <Button
            border
            shape="rectangle"
            variant="semi-transparent"
            css={`
              margin-top: var(--spacing-xs);
            `}
            onClick={onRequestClose}
          >
            Cancel
          </Button>
        </div>
      </Flex>
    );
  }

  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      innerPadding="0"
      css={`
        height: 100%;
        max-height: 100%;
        border-radius: 0;
        ${smallScreenAndAbove(css`
          height: auto;
          max-height: auto;
          border-radius: var(--spacing-xs);
        `)}
      `}
    >
      {renderContent()}
    </Modal>
  );
};

interface SecondaryPurchaseModalProps {
  dropId: string;
  mintAddress: string;
  listingId: string;
  open: boolean;
  onRequestClose: () => void;
}

export const SecondaryPurchaseModal = ({
  mintAddress,
  dropId,
  listingId,
  open,
  onRequestClose,
}: SecondaryPurchaseModalProps) => {
  useDisableScroll(open);

  const queryArgs: FetchWhereArgs = useMemo(
    () => ({
      collection: "marketplace_listings",
      where: [["mint", "==", mintAddress]],
    }),
    [mintAddress]
  );
  const cacheKey = useFirestoreCacheKey(queryArgs);
  const listingQuery = useFirestoreQuery<MarketplaceListing>(queryArgs);
  const listing: MarketplaceListing = listingQuery.data?.[0];

  const mintQuery = useQuery(
    ["mint", mintAddress],
    () => fetchMetadataByMintId(mintAddress),
    {
      enabled: Boolean(mintAddress),
    }
  );
  const mint = mintQuery.data;

  const socialWalletAddress = useSocialWalletAddress();
  const balanceQuery = useQuery(
    ["escrow_payment_account", socialWalletAddress],
    () => getPaymentTokenBalance(connection, socialWalletAddress)
  );

  const hasSufficientFunds = useMemo(() => {
    if (!balanceQuery.data || !listing?.amount) return false;
    return balanceQuery.data >= listing.amount;
  }, [balanceQuery.data, listing]);

  const royalties = useMemo(() => {
    if (!listing?.amount || !mint) return 0;
    return listing.amount * (mint.nft.sellerFeeBasisPoints / 10_000);
  }, [listing, mint]);

  const { connection } = useConnection();
  const router = useRouter();
  const pushSnack = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async () => {
      const tokenAccount = await connection.getTokenLargestAccounts(
        new PublicKey(mint.nft.address)
      );

      if (!tokenAccount.value) {
        throw new Error("No token account found");
      }

      return buyWithSocialWallet(listingId, {
        amount: listing.amount,
        seller: listing.seller,
        sellerHashedGuid: listing.sellerHashedGuid,
        tokenAccount: tokenAccount.value[0].address.toBase58(),
        mint: mint.nft.address,
        creators: mint.nft.creators.map((creator) => creator.address),
      });
    },
    {
      onError(err) {
        console.log("error: ", err);
        pushSnack({
          key: "buy-now-error",
          message: "Something went wrong",
          type: "error",
        });
      },
      async onSuccess(transactionId) {
        await queryClient.invalidateQueries(cacheKey);

        router.push({
          pathname: `/drop/${dropId}/purchase-success`,
          query: {
            dropId,
            isSecondary: true,
            payment_intent: transactionId,
          },
        });
      },
    }
  );

  const [deposit, setDeposit] = useState(false);

  function renderContent() {
    if (listingQuery.isLoading || mintQuery.isLoading) {
      return <LoadingPlaceholder />;
    }

    if (deposit) {
      return <ManageFunds onBackClick={() => setDeposit(false)} />;
    }

    return (
      <Flex
        direction="column"
        justify="flex-start"
        height="100%"
        padding="xs"
        paddingTop="md"
        paddingBottom="md"
        mdPadding="md"
        css={`
          position: relative;
        `}
      >
        <Headline
          css={`
            text-align: center;
            margin-bottom: var(--spacing-md);
          `}
        >
          Purchase Vault
        </Headline>
        <Panel>
          <Flex direction="column" width="100%">
            <Footnote
              css={`
                text-align: left;
              `}
            >
              Vault Balance
            </Footnote>
            <Flex align="center" justify="center">
              <Flex flex={1}>
                <LargeTitle>
                  {balanceQuery.data
                    ? formatDollarsFromCents(balanceQuery.data / 10_000)
                    : "$0.00"}
                </LargeTitle>
              </Flex>
              <Flex width="110px">
                <Button
                  border
                  shape="rectangle"
                  variant="semi-transparent"
                  fontSize={13}
                  css={`
                    margin-top: 0;
                    height: 32px !important;
                    padding: var(--spacing-sm);
                    white-space: nowrap;
                  `}
                  onClick={() => setDeposit(true)}
                >
                  Add Funds
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Panel>
        <Divider />
        <Flex
          css={`
            margin-bottom: var(--spacing-md);
          `}
        >
          <Image
            alt="Vault Image"
            src={mint.metadata.image}
            height={42}
            width={42}
            style={{
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "4px",
            }}
          />
          <Flex
            direction="column"
            css={`
              padding-left: var(--spacing-sm);
            `}
          >
            <Headline>{mint.nft.name}</Headline>
            <Subhead
              css={`
                color: var(--highlight-text);
                font-weight: 600;
                text-align: left;
              `}
            >
              {mint.creators?.[0]?.name}
            </Subhead>
          </Flex>
        </Flex>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell>Edition #</TableHeadCell>
              <TableHeadCell colSpan={2}>Rarity</TableHeadCell>
              <TableHeadCell>Price</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{mint.metadata.collectionNumber}</TableCell>
              <TableCell colSpan={2}>
                {/* @ts-ignore */}
                {mint.metadata.rarity ?? "Common"}
              </TableCell>
              <TableCell>
                {formatDollarsFromCents(listing.amount / 10_000)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Divider />
        <Footnote
          css={`
            text-align: left;
            margin-top: var(--spacing-md);
          `}
        >
          Total
        </Footnote>
        <LargeTitle
          css={`
            text-align: left;
          `}
        >
          {formatDollarsFromCents(listing.amount / 10_000)}
        </LargeTitle>
        <Flex>
          <Flex flex={1}>
            <Footnote>
              Artist Royalty ({mint.nft.sellerFeeBasisPoints / 100}%)
            </Footnote>
          </Flex>
          <Flex>
            <Footnote>
              {royalties ? formatDollarsFromCents(royalties / 10_000) : null}
            </Footnote>
          </Flex>
        </Flex>
        <Caption1
          css={`
            text-align: left;
            margin-top: var(--spacing-md);
          `}
        >
          By purchasing you agree to our{" "}
          <a href="/terms-of-service" target="_blank">
            terms of use
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank">
            privacy policy
          </a>
          .
        </Caption1>
        <Divider />
        <div
          css={`
            margin-top: auto;
          `}
        >
          <Button
            shape="rectangle"
            variant="primary"
            loading={mutation.isLoading}
            disabled={!hasSufficientFunds}
            onClick={() => mutation.mutate()}
          >
            Pay {formatDollarsFromCents(listing.amount / 10_000)}
          </Button>
          <Button
            border
            shape="rectangle"
            variant="semi-transparent"
            css={`
              margin-top: var(--spacing-xs);
            `}
            onClick={onRequestClose}
          >
            Cancel
          </Button>
        </div>
      </Flex>
    );
  }

  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      innerPadding="0"
      css={`
        height: 100%;
        max-height: 100%;
        border-radius: 0;
        ${smallScreenAndAbove(css`
          max-height: 632px;
          border-radius: var(--spacing-xs);
        `)}
      `}
    >
      {renderContent()}
    </Modal>
  );
};

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: var(--spacing-md) 0;
  background-color: rgb(var(--black-mid));
`;

const Table = styled.table`
  text-align: left;
  width: 100%;
  border-spacing: 0;
`;
const TableHead = styled.thead``;
const TableHeadCell = styled.th`
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  padding: 6px 8px;
`;
const TableBody = styled.tbody`
  background-color: rgba(255, 255, 255, 0.05);
`;
const TableRow = styled.tr``;
const TableCell = styled.td`
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 6px 8px;
`;
